import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import photoSelfieGuideImage from '../../../assets/images/registration-photo-selfie-guide.png';
import photoSoloGuideImage from '../../../assets/images/registration-photo-solo-guide.png';
import { zIndexMap } from '../../../shared/config/z-index';
import {
  BaseButton,
  PrimaryButton,
  mainHeadingTypographyStyles,
  nonSelectableSurfaceStyles,
  rubikTypographyStyles,
} from '../../../shared/ui';

type RegistrationPhotoScreenProps = {
  onPhotoSelect: (photo: File) => void;
  onSkip: () => void;
};

const Screen = styled.div.attrs({
  'data-ui': 'registration-photo-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${nonSelectableSurfaceStyles};

  & > [data-ui='registration-photo-heading'] {
    margin-left: ${({ theme }) => theme.scale.px(31)};
    margin-bottom: ${({ theme }) => theme.scale.px(43)};
  }

  & > [data-ui='registration-photo-illustration-stage'] {
    align-self: center;
    margin-bottom: auto;
  }
`;

const Heading = styled.div.attrs({
  'data-ui': 'registration-photo-heading',
})`
  width: ${({ theme }) => theme.scale.px(341)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const Title = styled.h1`
  ${mainHeadingTypographyStyles};
  line-height: ${({ theme }) => theme.scale.px(28)};
  white-space: pre-line;
`;

const Subtitle = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
  white-space: pre-line;
`;

const IllustrationStage = styled.div.attrs({
  'data-ui': 'registration-photo-illustration-stage',
})`
  width: ${({ theme }) => theme.scale.px(356)};
  height: ${({ theme }) => theme.scale.px(346)};
  position: relative;
`;

const GuideNote = styled.div<{
  $leftPx?: number;
  $rightPx?: number;
  $topPx: number;
  $widthPx: number;
}>`
  min-height: ${({ theme }) => theme.scale.px(46)};
  width: ${({ $widthPx, theme }) => theme.scale.px($widthPx)};
  padding: ${({ theme }) => `${theme.scale.px(6)} ${theme.scale.px(6)}`};
  position: absolute;
  top: ${({ $topPx, theme }) => theme.scale.px($topPx)};
  left: ${({ $leftPx, theme }) =>
    $leftPx === undefined ? 'auto' : theme.scale.px($leftPx)};
  right: ${({ $rightPx, theme }) =>
    $rightPx === undefined ? 'auto' : theme.scale.px($rightPx)};
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.scale.px(15)};
  background: ${({ theme }) => theme.colors.support.infoSubtle};
  box-shadow: 0 ${({ theme }) => theme.scale.px(4)}
    ${({ theme }) => theme.scale.px(4)} 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  text-align: center;
  z-index: ${zIndexMap.registration.photoGuideNote};
`;

const GuideNoteText = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(17)};
`;

const GuideCard = styled.div<{
  $leftPx?: number;
  $topPx: number;
}>`
  width: ${({ theme }) => theme.scale.px(171)};
  height: ${({ theme }) => theme.scale.px(201)};
  position: absolute;
  top: ${({ $topPx, theme }) => theme.scale.px($topPx)};
  left: ${({ $leftPx, theme }) =>
    $leftPx === undefined ? 'auto' : theme.scale.px($leftPx)};
  display: block;
  z-index: ${zIndexMap.registration.photoGuideCard};
`;

const GuideGraphic = styled.img.attrs({
  alt: '',
  'aria-hidden': true,
  draggable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  user-select: none;
  -webkit-user-select: none;
`;

const Footer = styled.div.attrs({
  'data-ui': 'registration-photo-actions',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(13)};
  padding-bottom: ${({ theme }) => theme.scale.px(8)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'registration-photo-actions-secondary',
})`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(36)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

const ChoosePhotoButton = styled(PrimaryButton)`
  align-self: center;
`;

const SkipButton = styled(BaseButton)`
  ${rubikTypographyStyles};
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: 1.25;
  text-decoration: underline;
`;

const HiddenPhotoInput = styled.input.attrs({
  accept: 'image/*',
  tabIndex: -1,
  type: 'file',
})`
  display: none;
`;

export function RegistrationPhotoScreen({
  onPhotoSelect,
  onSkip,
}: RegistrationPhotoScreenProps) {
  const { t } = useTranslation();
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const handleChoosePhoto = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedPhoto = event.currentTarget.files?.[0];

    event.currentTarget.value = '';

    if (!selectedPhoto) {
      return;
    }

    onPhotoSelect(selectedPhoto);
  };

  return (
    <Screen>
      <Heading>
        <Title>{t('registrationPhoto.title')}</Title>
        <Subtitle>{t('registrationPhoto.subtitle')}</Subtitle>
      </Heading>

      <IllustrationStage>
        <GuideNote $rightPx={16} $topPx={0} $widthPx={139}>
          <GuideNoteText>{t('registrationPhoto.selfieTip')}</GuideNoteText>
        </GuideNote>
        <GuideCard $leftPx={185} $topPx={36}>
          <GuideGraphic src={photoSelfieGuideImage} />
        </GuideCard>

        <GuideNote $leftPx={7} $topPx={109} $widthPx={158}>
          <GuideNoteText>{t('registrationPhoto.soloTip')}</GuideNoteText>
        </GuideNote>
        <GuideCard $leftPx={0} $topPx={145}>
          <GuideGraphic src={photoSoloGuideImage} />
        </GuideCard>
      </IllustrationStage>

      <Footer>
        <ChoosePhotoButton type="button" onClick={handleChoosePhoto}>
          {t('registrationPhoto.chooseCta')}
        </ChoosePhotoButton>
        <FooterSecondary>
          <SkipButton type="button" onClick={onSkip}>
            {t('registrationPhoto.skip')}
          </SkipButton>
        </FooterSecondary>
      </Footer>

      <HiddenPhotoInput ref={photoInputRef} onChange={handlePhotoChange} />
    </Screen>
  );
}
